import { ReactNode } from "react"
import { create } from "zustand"

import { useGlobalDialogStore } from "./use-global-dialog-store"

interface DialogOpenProps {
  content: Omit<PinnedDialog, "dialogContentClasses">
  closeCallback?: () => void
  dialogContentClasses?: string
}

interface PinnedDialog {
  content: ReactNode
  bookId?: string
  dialogContentClasses: string
  id: string
  type: "email" | "task" | "client" | "prospect" | "contact" | "note"
}

interface PinnedDialogState {
  pinnedDialogs: PinnedDialog[]
  fullScreenDialogId: string | null
  pinNewDialog: (props: DialogOpenProps) => void
  unpinDialog: (id: string) => void
  closeDialog: (id: string) => void
  closeAllDialogs: () => void
  afterClose: () => void
  toggleFullScreen: (id: string) => void
  exitFullScreen: (id: string) => void
  updateDialogId: (
    oldId: string,
    newId: string,
    type: "email" | "task" | "client" | "prospect" | "contact" | "note"
  ) => void
}

export const usePinnedDialogStore = create<PinnedDialogState>((set) => ({
  pinnedDialogs: [],
  fullScreenDialogId: null,
  pinNewDialog: ({ content, closeCallback = () => {}, dialogContentClasses = "" }) =>
    set((state) => ({
      pinnedDialogs: [...state.pinnedDialogs, { ...content, dialogContentClasses }],
      afterClose: closeCallback,
    })),
  closeDialog: (id: string) =>
    set((state) => {
      const newStack = [...state.pinnedDialogs]
      const index = newStack.findIndex((d) => d.id === id)
      if (index > -1) {
        newStack.splice(index, 1)
      }
      return {
        pinnedDialogs: newStack,
        fullScreenDialogId: state.fullScreenDialogId === id ? null : state.fullScreenDialogId,
      }
    }),
  closeAllDialogs: () => {
    set((state) => {
      state.afterClose()
      return {
        pinnedDialogs: [],
        fullScreenDialogId: null,
      }
    })
  },
  afterClose: () => {},
  unpinDialog: (id: string) =>
    set((state) => {
      const newStack = [...state.pinnedDialogs]
      const index = newStack.findIndex((d) => d.id === id)
      const contentToOpen = newStack[index]
      if (index > -1) {
        newStack.splice(index, 1)
      }
      const { openDialog } = useGlobalDialogStore.getState()
      openDialog({
        variant: "custom",
        content: contentToOpen.content,
        bookId: contentToOpen.bookId,
        dialogContentClasses: contentToOpen.dialogContentClasses,
        preserveOnNavigation: contentToOpen.type === "email",
      })
      return {
        pinnedDialogs: newStack,
        fullScreenDialogId: state.fullScreenDialogId === id ? null : state.fullScreenDialogId,
      }
    }),

  toggleFullScreen: (id: string) =>
    set((state) => ({
      fullScreenDialogId: state.fullScreenDialogId === id ? null : id,
    })),
  exitFullScreen: (id: string) =>
    set((state) => ({
      fullScreenDialogId: state.fullScreenDialogId === id ? null : state.fullScreenDialogId,
    })),

  updateDialogId: (oldId: string, newId: string, type: "email" | "task" | "client" | "prospect" | "contact" | "note") =>
    set((state) => {
      const newStack = [...state.pinnedDialogs]
      const index = newStack.findIndex((d) => d.id === oldId)

      if (index > -1) {
        const { createDialog } = useGlobalDialogStore.getState()
        const updatedContent = createDialog(newId, type)

        newStack[index] = {
          ...newStack[index],
          content: updatedContent,
          id: newId,
          type,
        }
      }

      return {
        pinnedDialogs: newStack,
        fullScreenDialogId: state.fullScreenDialogId === oldId ? newId : state.fullScreenDialogId,
      }
    }),
}))
